import {
  AlertCircle,
  BadgeCheck,
  Bell,
  Calendar,
  Gift,
  RefreshCw,
  Settings,
  Star,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyStateWithError, QueryErrorDisplay } from "@/lib/components/errors";
import { getUserFriendlyErrorMessage } from "@/lib/error/error-utils";
import { cn } from "@/lib/utils";
import { useNotifications } from "../../hooks";
import type { Notification } from "../../types";
import { formatNotificationTime, getNotificationCategoryIcon } from "../../utils";
import {
  handleNotificationNavigation,
  isNotificationActionable,
} from "../../utils/navigation-helpers";

const IconMap = {
  Calendar,
  Star,
  Gift,
  Settings,
  Bell,
};

interface NotificationListDesktopProps {
  className?: string;
  onOpenDialog?: (type: string, id: string, params?: Record<string, any>) => void;
}

export function NotificationListDesktop({ className, onOpenDialog }: NotificationListDesktopProps) {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Main hook
  const { notifications, loading, isLoadingMore, error, hasMore, retry, loadMore, markAsRead } =
    useNotifications();

  const handleMarkAsRead = async (ids: string[], markAll = false) => {
    await markAsRead(ids, markAll);
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read immediately when clicked
    if (!notification.isRead) {
      handleMarkAsRead([notification.id]);
    }

    // Use the new navigation system with dialog support
    handleNotificationNavigation(notification, undefined, onOpenDialog);
  };

  // Intersection observer for infinite scroll
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !loading && !isLoadingMore) {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }

          timeoutId = setTimeout(() => {
            loadMore();
          }, 200);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      observer.disconnect();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [hasMore, loading, isLoadingMore, loadMore]);

  // Loading state - show spinner for initial load
  if (loading && notifications.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state - show error display for initial load failures
  if (error && notifications.length === 0) {
    return <QueryErrorDisplay error={error} onRetry={retry} className={className} />;
  }

  return (
    <div className={cn("space-y-6", className)}>
      {/* Error alert for ongoing operations */}
      {error && notifications.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{getUserFriendlyErrorMessage(error)}</AlertDescription>
        </Alert>
      )}

      {/* Notifications Table */}
      {notifications.length === 0 ? (
        <EmptyStateWithError
          title="No notifications"
          description="You're all caught up!"
          error={null}
          onRetry={retry}
          actionButton={null}
          icon={<Bell className="h-12 w-12 text-muted-foreground" />}
        />
      ) : (
        <>
          <div className="border rounded-lg bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16" />
                  <TableHead className="min-w-0">Notification</TableHead>
                  <TableHead className="w-20 text-center">Category</TableHead>
                  <TableHead className="w-32">Time</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notifications.map((notification) => {
                  const categoryIconName = getNotificationCategoryIcon(notification.category);
                  const CategoryIcon = IconMap[categoryIconName as keyof typeof IconMap] || Bell;
                  const isActionableNotification = isNotificationActionable(notification);

                  return (
                    <TableRow
                      key={notification.id}
                      className={cn(isActionableNotification && "cursor-pointer hover:bg-muted/50")}
                      onClick={
                        isActionableNotification
                          ? () => handleNotificationClick(notification)
                          : undefined
                      }
                    >
                      <TableCell className="w-16 text-center">
                        {!notification.isRead && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full mx-auto" />
                        )}
                      </TableCell>
                      <TableCell className="min-w-0">
                        <div className="w-full">
                          <p
                            className={cn(
                              "font-medium text-sm mb-1",
                              !notification.isRead && "font-semibold"
                            )}
                          >
                            {notification.title}
                          </p>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                            {notification.message}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="w-20 text-center">
                        <CategoryIcon className="h-4 w-4 text-muted-foreground mx-auto" />
                      </TableCell>
                      <TableCell className="w-32 text-sm text-muted-foreground">
                        {formatNotificationTime(notification.createdAt)}
                      </TableCell>
                      <TableCell>
                        {!notification.isRead && (
                          <Button
                            variant="ghost"
                            size="default"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead([notification.id]);
                            }}
                          >
                            <BadgeCheck />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Load more trigger */}
          {hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-6">
              {isLoadingMore ? (
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Loading more notifications...
                  </span>
                </div>
              ) : (
                <div className="h-4 w-4" />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
